/**
 * The address fields of the resolve-label-error flow. Shared because both address cards edit the
 * same DTO and differ only in wording and in which of its optional fields they have a use for.
 * What a card puts around the fields is the card's own; this is the fields and nothing else.
 */
import { observer } from "mobx-react";
import { FormField } from "../../../../../../../components/form-field/form-field";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../../../components/fonts/font-props";
import PhoneInput from "./phone-input/phone-input";
import { SelectDropdown } from "../../../../../../../components/inputs/select-dropdown/select-dropdown";
import { IValidationGroup } from "../../../../../../../hooks/validation-group/validation-group-hook";
import AddressStore from "../../../../stores/rma-action-store/address-store";
import AddressField from "./address-field/address-field";
import "./shipment-address-form.scss";

/** The same hint on both address cards, so the form owns it instead of taking it twice. */
const PHONE_TOOLTIP = "Most carriers will require a valid phone number fitting the customer's zip code.";

interface IProps {
    store: AddressStore;
    validationGroup: IValidationGroup;
    nameLabel: string;
    /** Only the customer is reachable by email; the return address has no use for one. */
    showEmail?: boolean;
    /** Only the warehouse the parcel goes back to has a company name on the label. */
    showCompany?: boolean;
}

const ShipmentAddressForm: React.FC<IProps> = observer(({
    store,
    validationGroup,
    nameLabel,
    showEmail = false,
    showCompany = false,
}) => {
    return (
        <div className="shipment-address-form">
            <AddressField
                label={nameLabel}
                value={store.name}
                onChange={store.setName}
                validationGroup={validationGroup}
                required
            />

            {showEmail && (
                <AddressField
                    label="Customer email"
                    value={store.email}
                    onChange={store.setEmail}
                    validationGroup={validationGroup}
                    required
                />
            )}

            {showCompany && (
                <AddressField
                    label="Company name"
                    value={store.company}
                    onChange={store.setCompany}
                    validationGroup={validationGroup}
                />
            )}

            <AddressField
                label={<>Address 1 <FontBody2 color={FONT_COLOR.NEUTRAL3}>(Street name and number)</FontBody2></>}
                value={store.street1}
                onChange={store.setStreet1}
                validationGroup={validationGroup}
                required
            />

            <AddressField
                label="Address 2"
                value={store.street2}
                onChange={store.setStreet2}
                validationGroup={validationGroup}
            />

            <FormField label="Country" required>
                <SelectDropdown
                    value={store.selectedCountry}
                    options={store.countryOptions}
                    onChange={(option) => store.setCountry(option ? String(option.value) : null)}
                    required
                    validationGroup={validationGroup}
                />
            </FormField>

            <AddressField
                label="City"
                value={store.city}
                onChange={store.setCity}
                validationGroup={validationGroup}
                required
            />

            <AddressField
                label="Zip"
                value={store.zip}
                onChange={store.setZip}
                validationGroup={validationGroup}
                required
            />

            <AddressField
                label="State / Province code"
                value={store.state}
                onChange={store.setState}
                validationGroup={validationGroup}
            />

            <AddressField
                label="State / Province name"
                value={store.stateName}
                onChange={store.setStateName}
                validationGroup={validationGroup}
            />

            <FormField label="Phone" tooltip={PHONE_TOOLTIP} required>
                <PhoneInput
                    phone={store.phone}
                    options={store.callingCodeOptions}
                    required
                    validationGroup={validationGroup}
                />
            </FormField>
        </div>
    );
});

export default ShipmentAddressForm;
